import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { Link } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import { getSipList } from "../../../api/bseApi";

/**
 * SipListScreen - Lists SIPs for a user/client
 * Opens from User Listing with userId, or with clientCode in state
 * Provides links to Pause/Resume and Cancel
 */
export default function SipListScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const { userId } = useParams();
  const params = new URLSearchParams(location.search);
  const state = location.state;
  const [context, setContext] = useState(null);
  const [sips, setSips] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const clientCode = state?.clientCode || params.get("clientCode");
    if (clientCode) {
      setContext({ clientCode });
      return;
    }
    if (userId) {
      const fetchUser = async () => {
        try {
          const res = await axios.get(
            `${process.env.REACT_APP_BACKEND_BASE_URL || "http://localhost:8000/api"}/user/${userId}`
          );
          const user = res.data?.status === 1 ? res.data?.data : null;
          if (!user) {
            Swal.fire("Error", "User not found.", "error").then(() => navigate("/user"));
            return;
          }
          const ucc = user.ucc ?? user.UCC ?? "";
          if (!ucc || String(ucc).trim() === "" || String(ucc) === "Reject") {
            Swal.fire("Error", "ClientCode (UCC) not available.", "error").then(() => navigate("/user"));
            return;
          }
          setContext({ clientCode: String(ucc).trim() });
        } catch (err) {
          Swal.fire("Error", err.response?.data?.error || "Failed to load user.", "error").then(() => navigate("/user"));
        } finally {
          setLoading(false);
        }
      };
      fetchUser();
      return;
    }
    Swal.fire("Error", "Missing ClientCode. Open from User Listing.", "error").then(() => navigate("/user"));
    setLoading(false);
  }, [location.state, location.search, userId, navigate]);

  useEffect(() => {
    if (!context?.clientCode) return;
    const loadSips = async () => {
      setLoading(true);
      try {
        const res = await getSipList({ clientCode: context.clientCode });
        if (res.data?.status === 1 && Array.isArray(res.data?.data)) {
          setSips(res.data.data);
        }
      } catch (err) {
        console.warn("SIP list load failed:", err.message);
      } finally {
        setLoading(false);
      }
    };
    loadSips();
  }, [context?.clientCode]);

  if (!context) {
    return (
      <div className="main-content">
        <div className="page-wrapper page-settings">
          <div className="content p-4">
            <div className="text-center">
              <span className="spinner-border spinner-border-sm me-2" role="status" />
              Loading...
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="main-content">
      <div className="page-wrapper page-settings">
        <div className="content">
          <div
            className="mb-4"
            style={{
              background: "#ffffff",
              borderRadius: "8px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              padding: "20px",
            }}
          >
            <nav aria-label="breadcrumb" style={{ marginBottom: "15px" }}>
              <ol className="breadcrumb">
                <li className="breadcrumb-item"><Link to="/dashboard">Dashboard</Link></li>
                <li className="breadcrumb-item"><Link to="/user">Users</Link></li>
                <li className="breadcrumb-item active">SIP List</li>
              </ol>
            </nav>
            <h5>SIP List</h5>
            <p className="text-muted mb-0">Client Code: {context.clientCode}</p>
          </div>

          <div
            style={{
              background: "#ffffff",
              borderRadius: "8px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              padding: "24px",
              border: "1px solid #e6eef0",
            }}
          >
            {loading ? (
              <div className="text-center py-4">
                <span className="spinner-border spinner-border-sm me-2" role="status" />
                Loading SIPs...
              </div>
            ) : sips.length === 0 ? (
              <p className="text-muted mb-0">No SIPs found for this client.</p>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover">
                  <thead>
                    <tr>
                      <th>REG ID</th>
                      <th>Scheme</th>
                      <th>Amount</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sips.map((sip) => (
                      <tr key={sip.id || sip.sip_reg_id}>
                        <td>{sip.sip_reg_id}</td>
                        <td>{sip.scheme_name || sip.scheme_cd}</td>
                        <td>₹{sip.installment_amount?.toLocaleString()}</td>
                        <td>
                          <span className={`badge ${sip.status === "active" ? "bg-success" : "bg-secondary"}`}>
                            {sip.status || "active"}
                          </span>
                        </td>
                        <td>
                          {sip.status === "active" && (
                            <>
                              {sip.mandate_id && (
                                <Link
                                  to="/user/mandate-shift"
                                  state={{
                                    clientCode: context.clientCode,
                                    regId: sip.sip_reg_id,
                                    schemeCd: sip.scheme_cd,
                                    schemeName: sip.scheme_name,
                                    oldMandateId: sip.mandate_id,
                                  }}
                                  className="btn btn-sm btn-outline-secondary me-1"
                                  title="Mandate Shift"
                                >
                                  <i className="fas fa-arrows-alt-h" />
                                </Link>
                              )}
                              <Link
                                to="/user/sip-to-xsip-shift"
                                state={{
                                  clientCode: context.clientCode,
                                  regId: sip.sip_reg_id,
                                  schemeCd: sip.scheme_cd,
                                  schemeName: sip.scheme_name,
                                  sipStatus: sip.status,
                                }}
                                className="btn btn-sm btn-outline-info me-1"
                                title="Shift to XSIP"
                              >
                                <i className="fas fa-exchange-alt" />
                              </Link>
                              <Link
                                to="/user/sip-pause-resume"
                                state={{
                                  clientCode: context.clientCode,
                                  regId: sip.sip_reg_id,
                                  schemeCd: sip.scheme_cd,
                                  schemeName: sip.scheme_name,
                                  sipStatus: sip.status,
                                }}
                                className="btn btn-sm btn-outline-warning me-1"
                                title="Pause / Resume"
                              >
                                <i className="fas fa-pause" />
                              </Link>
                              <Link
                                to="/user/sip-cancel"
                                state={{
                                  clientCode: context.clientCode,
                                  regId: sip.sip_reg_id,
                                  schemeCd: sip.scheme_cd,
                                  schemeName: sip.scheme_name,
                                }}
                                className="btn btn-sm btn-outline-danger"
                                title="Cancel SIP"
                              >
                                <i className="fas fa-times" />
                              </Link>
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className="mt-3">
              <Link to="/user" className="btn btn-secondary">Back to Users</Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
